import { useContext, useMemo } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import {
  FeatureFlag,
  isAIPromptSettingsEnabled,
} from '../../../../../utils/postHog';
import useSessionStore from '../../../../../hooks/useSessionStore';
import { ConfigurationType, configurationPagesConfig } from '../../../../config/configurationPages';
import ConfigurationMenuContext from '../../../../context/ConfigurationMenuContext';

const useConfigurationListConfig = () => {
  const { navigateToConfig } = useContext(ConfigurationMenuContext);

  const isAutomaticOnboardingMessagesEnabled = useFeatureFlagEnabled(FeatureFlag.AUTOMATIC_ONBOARDING_MESSAGES);
  const isPromptSettingsEnabled = isAIPromptSettingsEnabled();
  const { isCoachAssistant, isAdmin } = useSessionStore();

  const configurationListEntries = useMemo(() => {
    const entries = [
      {
        configurationId: ConfigurationType.PAYMENT,
        onClick: () => navigateToConfig(ConfigurationType.PAYMENT),
        icon: configurationPagesConfig[ConfigurationType.PAYMENT].icon,
        title: configurationPagesConfig[ConfigurationType.PAYMENT].title,
      },
      {
        configurationId: ConfigurationType.CHECKOUT_PAGE,
        onClick: () => navigateToConfig(ConfigurationType.CHECKOUT_PAGE),
        icon: configurationPagesConfig[ConfigurationType.CHECKOUT_PAGE].icon,
        title: configurationPagesConfig[ConfigurationType.CHECKOUT_PAGE].title,
      },
      {
        configurationId: ConfigurationType.WELCOME_MESSAGE,
        onClick: () => navigateToConfig(ConfigurationType.WELCOME_MESSAGE),
        icon: configurationPagesConfig[ConfigurationType.WELCOME_MESSAGE].icon,
        title: configurationPagesConfig[ConfigurationType.WELCOME_MESSAGE].title,
      },
      {
        // Sits under Welcome Message: same delivery surface, but revisitable rather
        // than one-shot at activation.
        configurationId: ConfigurationType.LIBRARY_DOCUMENTS,
        onClick: () => navigateToConfig(ConfigurationType.LIBRARY_DOCUMENTS),
        icon: configurationPagesConfig[ConfigurationType.LIBRARY_DOCUMENTS].icon,
        title: configurationPagesConfig[ConfigurationType.LIBRARY_DOCUMENTS].title,
      },
      {
        configurationId: ConfigurationType.ONBOARDING_MESSAGES,
        onClick: () => navigateToConfig(ConfigurationType.ONBOARDING_MESSAGES),
        icon: configurationPagesConfig[ConfigurationType.ONBOARDING_MESSAGES].icon,
        title: configurationPagesConfig[ConfigurationType.ONBOARDING_MESSAGES].title,
        disabled: !isAutomaticOnboardingMessagesEnabled,
      },
      {
        configurationId: ConfigurationType.CLIENT_JOURNEY_WEEKLY_MESSAGE,
        onClick: () => navigateToConfig(ConfigurationType.CLIENT_JOURNEY_WEEKLY_MESSAGE),
        icon: configurationPagesConfig[ConfigurationType.CLIENT_JOURNEY_WEEKLY_MESSAGE].icon,
        title: configurationPagesConfig[ConfigurationType.CLIENT_JOURNEY_WEEKLY_MESSAGE].title,
      },
      {
        configurationId: ConfigurationType.HABITS,
        onClick: () => navigateToConfig(ConfigurationType.HABITS),
        icon: configurationPagesConfig[ConfigurationType.HABITS].icon,
        title: configurationPagesConfig[ConfigurationType.HABITS].title,
      },
      {
        configurationId: ConfigurationType.CHECK_IN,
        onClick: () => navigateToConfig(ConfigurationType.CHECK_IN),
        icon: configurationPagesConfig[ConfigurationType.CHECK_IN].icon,
        title: configurationPagesConfig[ConfigurationType.CHECK_IN].title,
      },
      {
        configurationId: ConfigurationType.PRODUCTS,
        onClick: () => navigateToConfig(ConfigurationType.PRODUCTS),
        icon: configurationPagesConfig[ConfigurationType.PRODUCTS].icon,
        title: configurationPagesConfig[ConfigurationType.PRODUCTS].title,
      },
      {
        configurationId: ConfigurationType.UPSELL_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.UPSELL_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.UPSELL_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.UPSELL_SETTINGS].title,
        disabled: !isAdmin,
      },
      {
        configurationId: ConfigurationType.PROFILE_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.PROFILE_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.PROFILE_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.PROFILE_SETTINGS].title,
      },
      {
        configurationId: ConfigurationType.NOTIFICATION_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.NOTIFICATION_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.NOTIFICATION_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.NOTIFICATION_SETTINGS].title,
      },
      {
        configurationId: ConfigurationType.INSTAGRAM_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.INSTAGRAM_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.INSTAGRAM_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.INSTAGRAM_SETTINGS].title,
      },
      {
        configurationId: ConfigurationType.OTHER_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.OTHER_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.OTHER_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.OTHER_SETTINGS].title,
      },
      {
        configurationId: ConfigurationType.AI_PROMPT_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.AI_PROMPT_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.AI_PROMPT_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.AI_PROMPT_SETTINGS].title,
        disabled: !isPromptSettingsEnabled,
      },
      {
        configurationId: ConfigurationType.CHECK_IN_FEEDBACK_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.CHECK_IN_FEEDBACK_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.CHECK_IN_FEEDBACK_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.CHECK_IN_FEEDBACK_SETTINGS].title,
        disabled: !isCoachAssistant,
      },
      {
        configurationId: ConfigurationType.FORM_SETTINGS,
        onClick: () => navigateToConfig(ConfigurationType.FORM_SETTINGS),
        icon: configurationPagesConfig[ConfigurationType.FORM_SETTINGS].icon,
        title: configurationPagesConfig[ConfigurationType.FORM_SETTINGS].title,
      },
      {
        configurationId: ConfigurationType.BIRTHDAY_MESSAGE,
        onClick: () => navigateToConfig(ConfigurationType.BIRTHDAY_MESSAGE),
        icon: configurationPagesConfig[ConfigurationType.BIRTHDAY_MESSAGE].icon,
        title: configurationPagesConfig[ConfigurationType.BIRTHDAY_MESSAGE].title,
      },
    ];

    return entries;
  }, [
    isPromptSettingsEnabled,
    navigateToConfig,
    isCoachAssistant,
    isAdmin,
    isAutomaticOnboardingMessagesEnabled,
  ]);

  return {
    configurationListEntries,
  };
};

export default useConfigurationListConfig;
